/**
 * V2 admin — Feedback tab (2026-08-29).
 *
 * The other end of the footer's Tell Us box (src/ui/footer.js). Messages
 * arrive by email too, but an inbox is where feedback goes to be forgotten:
 * this is the queue you can actually work — read it, write down what you
 * did about it, mark it resolved, throw it away.
 *
 * Open messages lead by default, because the only question this tab has to
 * answer on sight is "is anything waiting on me?".
 *
 * The table is written by submit_feedback() and read by nobody but admins
 * (20260829_feedback.sql). If that migration hasn't been applied yet the
 * load 404s and we name the file on screen rather than failing blank —
 * there is no migration pipeline (docs/SCHEMA-STATE.md).
 */
import * as gateway from '../../core/gateway.js';
import { escapeHtml, debounce } from '../../../assets/js/utils.js';
import { showToast } from '../../ui/toast.js';
import { confirmDialog } from './confirm.js';
import { fetchAll } from './data.js';

const PER_PAGE = 25;

/** Status filter + free-text search, newest first. Exported for tests. */
export function filterFeedback(rows, { status = 'new', search = '' } = {}) {
  let result = rows.filter((f) => (status === 'all' ? true : f.status === status));
  if (search) {
    const term = search.toLowerCase();
    result = result.filter((f) =>
      (f.message || '').toLowerCase().includes(term) ||
      (f.username || '').toLowerCase().includes(term) ||
      (f.contact_email || '').toLowerCase().includes(term) ||
      (f.admin_note || '').toLowerCase().includes(term) ||
      (f.page_path || '').toLowerCase().includes(term));
  }
  return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

export function mount(el, ctx) {
  const { router } = ctx;
  let destroyed = false;
  let all = [];
  let loaded = false;
  let loadError = '';
  let status = 'new';
  let search = '';
  let page = 1;
  let editingNote = null; // id of the row whose note box is open

  async function load() {
    all = await fetchAll('feedback', {
      select: 'id,created_at,user_id,username,contact_email,message,page_path,user_agent,status,admin_note,resolved_at,notified_at',
      order: 'created_at.desc',
    });
    loaded = true;
  }

  const openCount = () => all.filter((f) => f.status === 'new').length;

  function shown() {
    return filterFeedback(all, { status, search });
  }

  function when(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString(undefined, {
      timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short',
    });
  }

  function whoHtml(f) {
    if (f.username) {
      return `<button type="button" class="admin-link" data-feedback-user="${escapeHtml(f.username)}"
        >@${escapeHtml(f.username)}</button>`;
    }
    if (f.contact_email) return `<span class="admin-cell-dim">${escapeHtml(f.contact_email)}</span>`;
    return '<span class="admin-cell-dim">Anonymous</span>';
  }

  function cardHtml(f) {
    const resolved = f.status === 'resolved';
    return `
      <article class="admin-feedback-item ${resolved ? 'is-resolved' : ''}">
        <header class="admin-feedback-head">
          <span class="admin-feedback-who">${whoHtml(f)}</span>
          <span class="admin-cell-dim">${escapeHtml(when(f.created_at))}</span>
          ${resolved ? '<span class="admin-pill">Resolved</span>' : '<span class="admin-pill admin-pill-open">Open</span>'}
        </header>
        <p class="admin-feedback-message">${escapeHtml(f.message || '')}</p>
        <p class="admin-feedback-meta">
          ${f.page_path ? `From <code>${escapeHtml(f.page_path)}</code>` : 'No page recorded'}
          ${f.contact_email ? ` · <a href="mailto:${escapeHtml(f.contact_email)}">${escapeHtml(f.contact_email)}</a>` : ''}
          ${f.notified_at ? '' : ' · <span title="The admin email never went out — the message is still here.">not emailed</span>'}
        </p>
        ${editingNote === f.id ? `
          <div class="admin-feedback-note-edit">
            <textarea class="admin-feedback-note-input" data-note-input rows="3"
              placeholder="What you did about it…">${escapeHtml(f.admin_note || '')}</textarea>
            <div class="admin-actions">
              <button type="button" class="admin-btn admin-btn-xs" data-note-cancel>Cancel</button>
              <button type="button" class="admin-btn admin-btn-xs" data-note-save="${f.id}">Save note</button>
            </div>
          </div>`
          : f.admin_note ? `<p class="admin-feedback-note"><strong>Note:</strong> ${escapeHtml(f.admin_note)}</p>` : ''}
        <div class="admin-actions">
          <button type="button" class="admin-btn admin-btn-xs" data-note-edit="${f.id}">
            ${f.admin_note ? 'Edit note' : 'Add note'}</button>
          <button type="button" class="admin-btn admin-btn-xs" data-feedback-toggle="${f.id}">
            ${resolved ? 'Reopen' : 'Mark resolved'}</button>
          <button type="button" class="admin-btn admin-btn-xs admin-btn-danger"
            data-feedback-delete="${f.id}">Delete</button>
        </div>
      </article>`;
  }

  function render() {
    if (loadError) {
      el.innerHTML = `<div class="v2-error">${escapeHtml(loadError)}</div>`;
      return;
    }
    if (!loaded) {
      el.innerHTML = '<div class="v2-loading">Opening the suggestion box…</div>';
      return;
    }
    const list = shown();
    const totalPages = Math.ceil(list.length / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;
    const pageRows = list.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const open = openCount();
    el.innerHTML = `
      <div class="admin-card admin-card-span">
        <div class="admin-toolbar">
          <div class="admin-segmented" role="group" aria-label="Filter by status">
            ${[['new', `Open${open ? ` (${open})` : ''}`], ['resolved', 'Resolved'], ['all', 'All']]
              .map(([id, label]) => `
                <button type="button" class="admin-btn ${status === id ? 'is-active' : ''}"
                  data-feedback-status="${id}" ${status === id ? 'aria-pressed="true"' : 'aria-pressed="false"'}
                  >${label}</button>`).join('')}
          </div>
          <input type="search" class="admin-search" data-feedback-search aria-label="Search feedback"
            placeholder="Search messages…" value="${escapeHtml(search)}" autocomplete="off">
          <span class="admin-muted">${list.length} of ${all.length}</span>
          <span class="admin-toolbar-spacer"></span>
          <button type="button" class="admin-btn" data-page-prev ${page <= 1 ? 'disabled' : ''}>←</button>
          <span class="admin-muted">Page ${page} of ${totalPages}</span>
          <button type="button" class="admin-btn" data-page-next ${page >= totalPages ? 'disabled' : ''}>→</button>
        </div>
        <p class="admin-muted">Messages from the footer’s Tell Us box. Notes are yours alone — the
          sender never sees them, and nothing here emails anybody back. Deleting is permanent.</p>
        <div class="admin-feedback-list">
          ${pageRows.length
            ? pageRows.map(cardHtml).join('')
            : `<p class="admin-empty-cell">${status === 'new'
              ? 'Nothing waiting on you. '
              : 'No messages match. '}</p>`}
        </div>
      </div>`;

    el.querySelector('[data-feedback-search]')?.addEventListener('input', debounce((e) => {
      const caret = e.target.selectionStart;
      search = e.target.value;
      page = 1;
      render();
      const again = el.querySelector('[data-feedback-search]');
      if (again) { again.focus(); again.setSelectionRange(caret, caret); }
    }, 300));
  }

  /** One PATCH, applied locally on success — no reload for a one-field edit. */
  async function patch(id, body, failMessage) {
    const { error } = await gateway.rawUpdate(gateway.TABLES.FEEDBACK, {
      filters: { id: `eq.${id}` },
      body,
    });
    if (destroyed) return false;
    if (error) {
      showToast(failMessage, { type: 'info' });
      return false;
    }
    all = all.map((f) => (f.id === id ? { ...f, ...body } : f));
    return true;
  }

  async function toggleStatus(id) {
    const row = all.find((f) => f.id === id);
    if (!row) return;
    const next = row.status === 'resolved' ? 'new' : 'resolved';
    const ok = await patch(id, {
      status: next,
      resolved_at: next === 'resolved' ? new Date().toISOString() : null,
    }, 'Couldn’t update that message.');
    if (!ok) return;
    showToast(next === 'resolved' ? 'Marked resolved.' : 'Reopened.', { type: 'success' });
    render();
  }

  async function saveNote(id) {
    const input = el.querySelector('[data-note-input]');
    if (!input) return;
    const note = input.value.trim();
    const ok = await patch(id, { admin_note: note || null }, 'Couldn’t save that note.');
    if (!ok) return;
    editingNote = null;
    showToast(note ? 'Note saved.' : 'Note cleared.', { type: 'success' });
    render();
  }

  async function remove(id) {
    const row = all.find((f) => f.id === id);
    if (!row) return;
    if (!(await confirmDialog({
      title: 'Delete this message?',
      message: 'It’s gone for good — the sender has no copy either. Resolving keeps it readable.',
    }))) return;
    const { error } = await gateway.rawDelete(gateway.TABLES.FEEDBACK, { filters: { id: `eq.${id}` } });
    if (destroyed) return;
    if (error) return showToast('Couldn’t delete that message.', { type: 'info' });
    all = all.filter((f) => f.id !== id);
    showToast('Message deleted.', { type: 'success' });
    render();
  }

  function onClick(event) {
    const t = event.target;
    const statusBtn = t.closest('[data-feedback-status]');
    if (statusBtn) {
      status = statusBtn.dataset.feedbackStatus;
      page = 1;
      editingNote = null;
      return render();
    }
    if (t.closest('[data-page-prev]')) { if (page > 1) { page--; render(); } return; }
    if (t.closest('[data-page-next]')) { page++; return render(); }
    const user = t.closest('[data-feedback-user]');
    if (user) return router.navigate('user', { username: user.dataset.feedbackUser });
    const noteEdit = t.closest('[data-note-edit]');
    if (noteEdit) { editingNote = noteEdit.dataset.noteEdit; return render(); }
    if (t.closest('[data-note-cancel]')) { editingNote = null; return render(); }
    const noteSave = t.closest('[data-note-save]');
    if (noteSave) return saveNote(noteSave.dataset.noteSave);
    const toggle = t.closest('[data-feedback-toggle]');
    if (toggle) return toggleStatus(toggle.dataset.feedbackToggle);
    const del = t.closest('[data-feedback-delete]');
    if (del) return remove(del.dataset.feedbackDelete);
  }

  render();
  load().then(() => { if (!destroyed) render(); })
    .catch(() => {
      if (destroyed) return;
      loadError = 'Couldn’t load the feedback. If supabase/migrations/20260829_feedback.sql '
        + 'hasn’t been run yet, that’s why.';
      render();
    });

  el.addEventListener('click', onClick);

  return () => {
    destroyed = true;
    el.removeEventListener('click', onClick);
  };
}
